/**
 * Cricket ball icons.
 *
 * An icon is either:
 *  - an image URL (e.g. `/icons/deepseek.svg`) drawn onto the canvas, or
 *  - a short glyph / emoji drawn as text (fallback for bots and custom picks).
 *
 * Provider presets ship a default logo URL; users can override with an emoji
 * in the duel modal. Slot letters A/B remain the last-resort fallback so a
 * match never renders a blank ball.
 */

import { slotTheme } from "./theme";

/** Built-in bot glyphs — readable without loading images. */
export const BOT_ICONS: Record<string, string> = {
  collector: "◆",
  scout: "◎",
  hunter: "⚔",
};

/** Emoji choices offered in the AI cricket form. */
export const ICON_CHOICES: readonly string[] = [
  "🔴",
  "🟣",
  "🟢",
  "🟡",
  "⚡",
  "👾",
  "💎",
  "🔥",
  "🌊",
  "🧿",
];

const imageCache = new Map<string, HTMLImageElement | "loading" | "failed">();

/** True when the string should be loaded as an image rather than drawn as text. */
export function isIconImage(icon: string): boolean {
  return (
    icon.startsWith("/") ||
    icon.startsWith("http://") ||
    icon.startsWith("https://") ||
    icon.startsWith("data:")
  );
}

/**
 * Returns a loaded image for `src`, kicking off a load if needed.
 *
 * Returns `null` while loading or after failure; the renderer falls back to
 * the slot letter until the image is ready on a later frame.
 */
export function getIconImage(src: string): HTMLImageElement | null {
  if (typeof Image === "undefined") return null;
  const cached = imageCache.get(src);
  if (cached instanceof HTMLImageElement) return cached;
  if (cached === "loading" || cached === "failed") return null;

  imageCache.set(src, "loading");
  const img = new Image();
  img.decoding = "async";
  img.onload = () => imageCache.set(src, img);
  img.onerror = () => imageCache.set(src, "failed");
  img.src = src;
  return null;
}

/** Prefetch provider logos so the first arena frame can use them. */
export function prefetchIcon(src: string | undefined | null): void {
  if (!src || !isIconImage(src)) return;
  getIconImage(src);
}

/** Resolve the icon to paint for a cricket, with slot-letter fallback. */
export function resolveIcon(
  icon: string | undefined | null,
  slot: number,
  cricketId?: string,
): string {
  if (icon && icon.trim()) return icon.trim();
  if (cricketId && BOT_ICONS[cricketId]) return BOT_ICONS[cricketId]!;
  return slotTheme(slot).label;
}

/**
 * Draw an icon centred on (x, y) inside a cricket ball of radius `base`.
 *
 * Official vendor logos sit on a white circular pad so mono marks (OpenAI,
 * Anthropic, xAI, …) stay legible on the slot-coloured body. Glyphs keep the
 * prior letter style. `loadingGlyph` is shown while an image is still fetching.
 */
export function drawCricketIcon(
  ctx: CanvasRenderingContext2D,
  icon: string,
  x: number,
  y: number,
  base: number,
  inkColor: string,
  loadingGlyph = "·",
): void {
  if (isIconImage(icon)) {
    const img = getIconImage(icon);
    if (img) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, base * 0.92, 0, Math.PI * 2);
      ctx.clip();
      // Photo avatars (e.g. Doubao PNG) already fill a circle — skip the white
      // pad so we don't get a halo. Vector brand marks need the pad so mono
      // paths stay legible on the slot-coloured body.
      const photo = /\.(png|jpe?g|webp)(\?|$)/i.test(icon);
      if (!photo) {
        ctx.fillStyle = "#ffffff";
        ctx.fill();
      }
      const size = photo ? base * 1.85 : base * 1.2;
      ctx.drawImage(img, x - size / 2, y - size / 2, size, size);
      ctx.restore();
      return;
    }
    drawGlyph(ctx, loadingGlyph, x, y, base, inkColor);
    return;
  }

  drawGlyph(ctx, icon, x, y, base, inkColor);
}

function drawGlyph(
  ctx: CanvasRenderingContext2D,
  glyph: string,
  x: number,
  y: number,
  base: number,
  color: string,
): void {
  // Dark disc behind text icons keeps contrast on bright slot colours.
  ctx.fillStyle = "#080a12";
  ctx.beginPath();
  ctx.arc(x, y, base * 0.46, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = color;
  const emoji = /\p{Extended_Pictographic}/u.test(glyph);
  ctx.font = emoji
    ? `${Math.max(10, base * 0.95)}px "Apple Color Emoji", "Segoe UI Emoji", sans-serif`
    : `bold ${Math.max(9, base * 0.95)}px ui-monospace, monospace`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(glyph, x, y + 0.5);
}
